import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { IOperativeDocumentSummary } from '../../../message-models/operative-document-summary.interface';
import { DocumentService } from '../../../documents/services/document.service';
import { DocumentModalService } from '../../../documents/services/document-modal.service';
import { DocumentEntityType, DocumentEntityTypeString } from '../../../message-models/document-entity-type.enum';
import { OperativeDocumentService } from '../../services/operative-document.service';
import { Router } from '@angular/router';
import { IBreadcrumb } from '../../../eca-breadcrumb/models/breadcrumb.interface';
import { EcaBreadcrumbService } from '../../../eca-breadcrumb/services/eca-breadcrumb.service';
import { CompanyDocumentService } from '../../../company/services/company-documents.service';
import { IDocumentSummaryInfo } from '../../../message-models/document-summary-info.interface';
import { IAllowedOperations, IUserInfo } from 'src/app/users/models/userInfo.interface';
import { consts } from 'src/app/common/modals/consts';
import { Subscription } from 'rxjs';
import { OurUserService } from 'src/app/users/services/user.service';
import { EcaConfirmProcessService } from '../../../shared/services/eca-confirm-process.service';
import { ErrorHandlingService } from '../../../services/error-handling.service';
import { HelpConsts } from '../../../help/models/help-consts';
import { HelpModalService } from '../../../help/services/help-modal.service';
import { IHelpInfo } from '../../../help/models/i-help-info.interface';
import { EcaConfirmDeleteModalOptions } from "../../../shared/models/confirm-delete-modal-options.class";

@Component({
  selector: 'app-operative-documents-page',
  templateUrl: './operative-documents-page.component.html',
  styleUrls: ['./operative-documents-page.component.scss'],
  encapsulation: ViewEncapsulation.None
})

export class OperativeDocumentsPageComponent implements OnInit, OnDestroy {
  operativeDocumentSummaries: IDocumentSummaryInfo[];
  loading: boolean;
  operativeDocumentsLoading: boolean = true;
  public loggedInUser: IUserInfo;
  private companyId: string = consts.emptyGuid;
  public operativeUpdateOperations: IAllowedOperations = {} as IAllowedOperations;
  private loggedInUserSubscription: Subscription;

  constructor(private operativeDocumentService: OperativeDocumentService,
    private companyDocumentService: CompanyDocumentService,
    private documentService: DocumentService,
    private documentModalService: DocumentModalService,
    private router: Router,
    private ecaBreadcrumbService: EcaBreadcrumbService,
    private userService: OurUserService,
    private helpService: HelpModalService,
    private confirmProcessService: EcaConfirmProcessService,
    private errorHandlingService: ErrorHandlingService
  ) {
  }

  ngOnInit(): void {
    this.userService.getLoggedInUser().subscribe(() => {
      this.loggedInUserSubscription = this.userService.getLoggedInUserSubject().subscribe(user => {
        this.updateUserAndReload(user);
        this.populateBreadcrumbs();
      })
    }, () => {
      this.errorHandlingService.showErrorWarning().subscribe(() => {
      });
    })
  }

  private updateUserAndReload(user: IUserInfo) {
    this.loggedInUser = user;
    this.companyId = this.loggedInUser?.companyId;
    if (this.loggedInUser?.operativeOperations?.canUpdate)
      this.operativeUpdateOperations = { canInsert: true, canUpdate: true, canRetrieve: true, canDelete: true } as IAllowedOperations;
    else
      this.operativeUpdateOperations = {} as IAllowedOperations;
    if (this.companyId !== consts.emptyGuid || this.companyId !== undefined) {
      this.getOperativeDocumentSummaries();
    }
  }

  getOperativeDocumentSummaries(includeExpired: boolean = false) {
    this.companyDocumentService.getAllDocumentSummariesForOperatives(this.companyId, includeExpired).subscribe(operativeDocumentSummaries => {
      operativeDocumentSummaries.forEach(x => {
        x.entityTypeString = DocumentEntityTypeString[x.entityType];
      })
      this.operativeDocumentSummaries = operativeDocumentSummaries;
      this.operativeDocumentsLoading = false;
    }, () => {
      this.errorHandlingService.showErrorWarning().subscribe(() => {
      });
    });
  }

  downloadOperativeDocument(operativeDocuments: IOperativeDocumentSummary) {
    this.documentService.downloadOrOpenDocument(operativeDocuments, true);
  }

  openOperativeDocument(operativeDocuments: IOperativeDocumentSummary) {
    this.documentService.downloadOrOpenDocument(operativeDocuments);
  }

  deleteOperativeDocumentClick(operativeDocument: IOperativeDocumentSummary) {
    this.confirmProcessService.openConfirmProcessModal('Are you sure you want to delete this document?',
      'Delete ' + operativeDocument.originalFilename, new EcaConfirmDeleteModalOptions()).subscribe({
        next: response => {
          if (response) {
            this.operativeDocumentsLoading = true;
            this.deleteOperativeDocument(operativeDocument);
          }
        },
        error: () => {
          this.errorHandlingService.showErrorWarning().subscribe(() => {
          });
        }
      });
  }

  deleteOperativeDocument(operativeDocument: IOperativeDocumentSummary) {
    this.operativeDocumentService.deleteOperativeDocument(this.companyId, operativeDocument.documentId).subscribe(
      result => {
        if (result) {
          // const documentIndex = this.operativeDocuments.findIndex(c => c.documentId !== operativeDocument.documentId);
          // this.operativeDocuments.splice(documentIndex, 1);
          this.getOperativeDocumentSummaries();
          this.operativeDocumentsLoading = false;

        }
      }, () => {
        this.errorHandlingService.showErrorWarning().subscribe(() => {
        });
      });
  }

  editOperativeDocument(operativeDocumentSummary: IOperativeDocumentSummary) {
    this.documentModalService.openDocumentUploaderModal(operativeDocumentSummary, DocumentEntityType.employedPerson, 'Edit Operative Document').subscribe(documentModalRef => {
      documentModalRef.content.documentUploadSave.subscribe((documentUploadRequest: FormData) => {
        documentModalRef.dynamicDialogRef.close();
        this.operativeDocumentsLoading = true;
        documentUploadRequest.append('OperativeId', operativeDocumentSummary.operativeId);
        documentUploadRequest.append('CompanyId', this.companyId);
        this.loading = true;
        this.operativeDocumentService.createOrUpdateOperativeDocument(documentUploadRequest).subscribe(updatedOperativeDocumentsSummary => {
          this.loading = false;
          // const documentIndex = this.operativeDocuments.findIndex(d => d.documentId === operativeDocumentSummary.documentId);
          // this.operativeDocuments[documentIndex] = updatedOperativeDocumentsSummary;
          this.getOperativeDocumentSummaries();
          this.operativeDocumentsLoading = false;

        }, () => {
          this.errorHandlingService.showErrorWarning().subscribe(() => {
          });
        });
      });
    });
  }

  viewOperativeDocument(operativeDocumentSummary: IOperativeDocumentSummary) {
    this.router.navigate(['operative-document', operativeDocumentSummary.documentId]);

  }

  private populateBreadcrumbs() {
    const operativeBreadCrumbs = new Array<IBreadcrumb>();
    operativeBreadCrumbs.push({ label: 'Operatives', iconName: 'users', routeLink: '/operatives' } as IBreadcrumb);
    operativeBreadCrumbs.push({ label: 'Operative Documents', iconName: 'books' } as IBreadcrumb);
    this.ecaBreadcrumbService.breadCrumbs.next(operativeBreadCrumbs);
  }

  ngOnDestroy(): void {
    this.loggedInUserSubscription.unsubscribe();
  }

  refreshDocuments(includeExpired: boolean) {
    this.getOperativeDocumentSummaries(includeExpired);
  }

  openOperativesDocumentsHelp() {
    this.helpService.openHelpModal(HelpConsts.AllOperativeDocumentsHelp);
  }
}
